import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, inr, resolveImg } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import DOMPurify from "dompurify";
import { AdminListings } from "../components/AdminListings";
import { AdminBidz } from "../components/AdminBidz";
import { AdminFx } from "../components/AdminFx";
import { ShieldCheck, Bold, Italic, Heading2, Heading3, Link as LinkIcon, List, ListOrdered, Quote, Image as ImageIcon, Eye, Edit3, Trash2, Plus, Gavel } from "lucide-react";

const BLOG_CATEGORIES = [
  "AuctionBulls News", "Auction Guides", "Buyer Guides", "Seller Guides",
  "Property", "Businesses for Sale", "Cars", "Bikes & Scooters",
  "Collectibles", "Market Updates", "Tips & Insights", "Announcements",
];

const SOCIAL_KEYS = [
  { key: "instagram", label: "Instagram" },
  { key: "facebook", label: "Facebook" },
  { key: "x", label: "X (Twitter)" },
  { key: "linkedin", label: "LinkedIn" },
  { key: "youtube", label: "YouTube" },
];

export default function AdminDashboard() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [stats, setStats] = useState({});
  const [pending, setPending] = useState([]);
  const [users, setUsers] = useState([]);
  const [audit, setAudit] = useState([]);
  const [settings, setSettings] = useState({ social: {} });
  const [posts, setPosts] = useState([]);
  const [disputes, setDisputes] = useState([]);
  const [editing, setEditing] = useState(null);
  const [activeDispute, setActiveDispute] = useState(null);

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/signin?next=/admin"); return; }
    if (!user.roles?.includes("admin")) { nav("/"); return; }
  }, [user, loading, nav]);

  const reload = () => {
    api.get("/admin/stats").then(r => setStats(r.data));
    api.get("/listings", { params: { status: "pending_review", limit: 100 } }).then(r => setPending(r.data));
    api.get("/admin/users").then(r => setUsers(r.data));
    api.get("/admin/audit").then(r => setAudit(r.data));
    api.get("/settings").then(r => setSettings({ ...r.data, social: r.data.social || {} }));
    api.get("/admin/blog/posts").then(r => setPosts(r.data));
    api.get("/admin/disputes").then(r => setDisputes(r.data));
  };
  useEffect(() => { if (user?.roles?.includes("admin")) reload(); }, [user]);

  const approve = async (id) => { await api.post(`/listings/${id}/approve`); toast.success("Approved"); reload(); };
  const reject = async (id) => { await api.post(`/listings/${id}/reject`); toast.info("Rejected"); reload(); };
  const suspend = async (id) => { await api.post(`/admin/users/${id}/suspend`); toast.info("Suspended"); reload(); };
  const restore = async (id) => { await api.post(`/admin/users/${id}/restore`); toast.success("Restored"); reload(); };

  const saveSettings = async () => {
    await api.put("/settings", {
      global_seller_fee_pct: Number(settings.global_seller_fee_pct),
      global_buyer_fee_pct: Number(settings.global_buyer_fee_pct),
      tax_pct: Number(settings.tax_pct),
      anti_snipe_threshold_sec: Number(settings.anti_snipe_threshold_sec),
      anti_snipe_extension_sec: Number(settings.anti_snipe_extension_sec),
      inspection_days: Number(settings.inspection_days),
      social: settings.social || {},
    });
    toast.success("Settings saved");
    reload();
  };

  const setSocial = (key, patch) => {
    setSettings(s => ({ ...s, social: { ...(s.social || {}), [key]: { ...(s.social?.[key] || {}), ...patch } } }));
  };

  const deletePost = async (id) => {
    if (!window.confirm("Delete this article?")) return;
    await api.delete(`/admin/blog/posts/${id}`);
    toast.success("Deleted");
    reload();
  };

  const openDispute = async (id) => {
    try { const r = await api.get(`/disputes/${id}`); setActiveDispute(r.data); }
    catch (e) { toast.error("Cannot load dispute"); }
  };

  const resolveDispute = async (resolution, refundAmount, note) => {
    try {
      const body = { resolution, note };
      if (resolution === "buyer_refund_partial") body.refund_amount = Number(refundAmount || 0);
      await api.post(`/admin/disputes/${activeDispute.id}/resolve`, body);
      toast.success("Dispute resolved");
      setActiveDispute(null); reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Resolve failed"); }
  };

  const changeDisputeStatus = async (status) => {
    try {
      await api.post(`/admin/disputes/${activeDispute.id}/status`, { status });
      toast.success("Status updated");
      const r = await api.get(`/disputes/${activeDispute.id}`);
      setActiveDispute(r.data); reload();
    } catch (e) { toast.error("Update failed"); }
  };

  if (!user?.roles?.includes("admin")) return null;

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-6 py-10">
      <div className="flex items-center gap-3 mb-2">
        <ShieldCheck className="h-6 w-6 text-amber-500" />
        <div className="text-xs uppercase tracking-[0.2em] font-bold text-amber-600">Admin Console</div>
      </div>
      <h1 className="text-3xl md:text-4xl font-extrabold display-serif">Platform Control</h1>

      <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat testid="admin-dashboard-gmv-stat" k="GMV" v={inr(stats.gmv)} />
        <Stat k="Platform revenue" v={inr(stats.platform_revenue)} />
        <Stat k="Active listings" v={stats.active_listings ?? 0} />
        <Stat k="Total users" v={stats.total_users ?? 0} />
        <Stat k="Total bids" v={stats.total_bids ?? 0} />
        <Stat k="Buyer fees" v={inr(stats.buyer_fees)} />
        <Stat k="Seller fees" v={inr(stats.seller_fees)} />
        <Stat k="Pending review" v={stats.pending_review ?? 0} tone="amber" />
      </div>

      <Tabs defaultValue="review" className="mt-10">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="review" data-testid="admin-tab-review">Listings ({pending.length})</TabsTrigger>
          <TabsTrigger value="all-listings" data-testid="admin-tab-all-listings">All Listings</TabsTrigger>
          <TabsTrigger value="bidz" data-testid="admin-tab-bidz">Bidz</TabsTrigger>
          <TabsTrigger value="currency" data-testid="admin-tab-currency">Currency</TabsTrigger>
          <TabsTrigger value="users">Users ({users.length})</TabsTrigger>
          <TabsTrigger value="disputes" data-testid="admin-tab-disputes">Disputes</TabsTrigger>
          <TabsTrigger value="blog" data-testid="admin-tab-blog">Blog ({posts.length})</TabsTrigger>
          <TabsTrigger value="settings" data-testid="admin-tab-settings">Settings</TabsTrigger>
          <TabsTrigger value="audit">Audit Log</TabsTrigger>
        </TabsList>

        <TabsContent value="review" className="mt-6">
          {pending.length === 0 ? <div className="text-slate-500 text-center py-12">No listings awaiting review.</div> : (
            <div className="rounded-2xl border border-slate-200 dark:border-slate-800 overflow-hidden bg-white dark:bg-slate-900">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 dark:bg-slate-800 text-left text-xs uppercase text-slate-500">
                  <tr><th className="px-4 py-3">Title</th><th className="px-4 py-3">Seller</th><th className="px-4 py-3">Start</th><th className="px-4 py-3">Flags</th><th className="px-4 py-3"></th></tr>
                </thead>
                <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                  {pending.map(l => (
                    <tr key={l.id}>
                      <td className="px-4 py-3 font-semibold">{l.title}</td>
                      <td className="px-4 py-3">{l.seller_name}</td>
                      <td className="px-4 py-3">{inr(l.starting_bid)}</td>
                      <td className="px-4 py-3 text-xs">{l.risk_flags?.join(", ") || "—"}</td>
                      <td className="px-4 py-3 flex gap-2">
                        <Button size="sm" onClick={() => approve(l.id)} className="bg-emerald-600 text-white">Approve</Button>
                        <Button size="sm" variant="outline" onClick={() => reject(l.id)}>Reject</Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        <TabsContent value="all-listings" className="mt-6">
          <AdminListings onChanged={reload} />
        </TabsContent>

        <TabsContent value="currency" className="mt-6">
          <AdminFx />
        </TabsContent>

        <TabsContent value="bidz" className="mt-6">
          <AdminBidz />
        </TabsContent>

        <TabsContent value="users" className="mt-6">
          <div className="rounded-2xl border border-slate-200 dark:border-slate-800 overflow-hidden bg-white dark:bg-slate-900">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 dark:bg-slate-800 text-left text-xs uppercase text-slate-500">
                <tr><th className="px-4 py-3">Name</th><th className="px-4 py-3">Email</th><th className="px-4 py-3">Roles</th><th className="px-4 py-3">Level</th><th className="px-4 py-3">Status</th><th className="px-4 py-3"></th></tr>
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                {users.slice(0, 60).map(u => (
                  <tr key={u.id}>
                    <td className="px-4 py-3 font-semibold">{u.name}</td>
                    <td className="px-4 py-3 text-xs">{u.email}</td>
                    <td className="px-4 py-3 text-xs">{u.roles?.join(", ")}</td>
                    <td className="px-4 py-3 capitalize">{u.seller_level}</td>
                    <td className="px-4 py-3">{u.suspended ? <span className="text-red-600 text-xs font-bold">SUSPENDED</span> : <span className="text-emerald-600 text-xs font-bold">ACTIVE</span>}</td>
                    <td className="px-4 py-3">
                      {u.suspended
                        ? <Button size="sm" variant="outline" onClick={() => restore(u.id)}>Restore</Button>
                        : <Button size="sm" variant="outline" onClick={() => suspend(u.id)}>Suspend</Button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* DISPUTES */}
        <TabsContent value="disputes" className="mt-6">
          <div className="rounded-2xl border border-slate-200 dark:border-slate-800 overflow-hidden bg-white dark:bg-slate-900" data-testid="admin-disputes-table">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 dark:bg-slate-800 text-left text-xs uppercase text-slate-500">
                <tr><th className="px-4 py-3">Case</th><th className="px-4 py-3">Listing</th><th className="px-4 py-3">Buyer</th><th className="px-4 py-3">Seller</th><th className="px-4 py-3">Reason</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Hold</th><th className="px-4 py-3"></th></tr>
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                {disputes.map(d => (
                  <tr key={d.id}>
                    <td className="px-4 py-3 font-mono text-xs">{d.id.slice(0,8)}</td>
                    <td className="px-4 py-3 font-semibold">{d.listing_title}</td>
                    <td className="px-4 py-3 text-xs">{d.buyer_name}</td>
                    <td className="px-4 py-3 text-xs">{d.seller_name}</td>
                    <td className="px-4 py-3 text-xs">{d.reason.replace(/_/g," ")}</td>
                    <td className="px-4 py-3"><span className="text-[10px] uppercase font-bold px-2 py-0.5 rounded bg-amber-100 text-amber-700">{d.status.replace(/_/g," ")}</span></td>
                    <td className="px-4 py-3 font-bold">{inr(d.payout_hold_amount)}</td>
                    <td className="px-4 py-3">
                      <Button size="sm" variant="outline" onClick={() => openDispute(d.id)} data-testid={`view-dispute-${d.id}`}>Review</Button>
                    </td>
                  </tr>
                ))}
                {disputes.length === 0 && <tr><td colSpan="8" className="text-center text-slate-500 py-10">No disputes.</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* BLOG */}
        <TabsContent value="blog" className="mt-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold">Blog Management</h3>
            <Button data-testid="blog-new-btn" onClick={() => setEditing({ title: "", excerpt: "", content: "", category: "AuctionBulls News", tags: [], status: "draft", featured: false, featured_image: "" })} className="bg-slate-900 text-white gap-1"><Plus className="h-4 w-4" /> New Article</Button>
          </div>
          <div className="rounded-2xl border border-slate-200 dark:border-slate-800 overflow-hidden bg-white dark:bg-slate-900">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 dark:bg-slate-800 text-left text-xs uppercase text-slate-500">
                <tr><th className="px-4 py-3">Title</th><th className="px-4 py-3">Category</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Views</th><th className="px-4 py-3">Updated</th><th className="px-4 py-3"></th></tr>
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                {posts.map(p => (
                  <tr key={p.id}>
                    <td className="px-4 py-3 font-semibold">{p.title} {p.featured && <span className="text-[10px] ml-1 px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded">FEATURED</span>}</td>
                    <td className="px-4 py-3 text-xs">{p.category}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded text-[10px] uppercase font-bold ${
                        p.status === "published" ? "bg-emerald-100 text-emerald-700"
                        : p.status === "scheduled" ? "bg-amber-100 text-amber-700"
                        : "bg-slate-100 text-slate-600"
                      }`}>{p.status}</span>
                    </td>
                    <td className="px-4 py-3">{p.views}</td>
                    <td className="px-4 py-3 text-xs text-slate-500">{new Date(p.updated_at).toLocaleDateString()}</td>
                    <td className="px-4 py-3 flex gap-2">
                      {p.status === "published" && <a data-testid={`blog-view-${p.id}`} href={`/blog/${p.slug}`} target="_blank" rel="noreferrer" className="text-slate-500 hover:text-amber-600" title="View"><Eye className="h-4 w-4" /></a>}
                      <button data-testid={`blog-edit-${p.id}`} onClick={() => setEditing(p)} className="text-slate-500 hover:text-amber-600" title="Edit"><Edit3 className="h-4 w-4" /></button>
                      <button data-testid={`blog-delete-${p.id}`} onClick={() => deletePost(p.id)} className="text-red-500 hover:text-red-700" title="Delete"><Trash2 className="h-4 w-4" /></button>
                    </td>
                  </tr>
                ))}
                {posts.length === 0 && <tr><td colSpan="6" className="text-center text-slate-500 py-10">No articles yet. Create your first one.</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* SETTINGS */}
        <TabsContent value="settings" className="mt-6 space-y-6">
          <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6">
            <h3 className="text-lg font-bold mb-4">Marketplace Settings</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <Setting label="Seller success fee (%)" v={settings.global_seller_fee_pct} onChange={v => setSettings({ ...settings, global_seller_fee_pct: v })} />
              <Setting label="Buyer protection fee (%)" v={settings.global_buyer_fee_pct} onChange={v => setSettings({ ...settings, global_buyer_fee_pct: v })} />
              <Setting label="Tax on fees (%)" v={settings.tax_pct} onChange={v => setSettings({ ...settings, tax_pct: v })} />
              <Setting label="Anti-snipe threshold (sec)" v={settings.anti_snipe_threshold_sec} onChange={v => setSettings({ ...settings, anti_snipe_threshold_sec: v })} />
              <Setting label="Anti-snipe extension (sec)" v={settings.anti_snipe_extension_sec} onChange={v => setSettings({ ...settings, anti_snipe_extension_sec: v })} />
              <Setting label="Inspection window (days)" v={settings.inspection_days} onChange={v => setSettings({ ...settings, inspection_days: v })} />
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6" data-testid="social-settings">
            <h3 className="text-lg font-bold mb-1">Social Media</h3>
            <p className="text-xs text-slate-500 mb-4">Only enabled platforms with a valid URL will appear in the footer.</p>
            <div className="space-y-3">
              {SOCIAL_KEYS.map(({ key, label }) => {
                const v = settings.social?.[key] || {};
                return (
                  <div key={key} className="grid grid-cols-12 gap-3 items-center">
                    <div className="col-span-3 md:col-span-2 text-sm font-semibold">{label}</div>
                    <div className="col-span-6 md:col-span-8">
                      <Input
                        data-testid={`social-${key}-url`}
                        value={v.url || ""}
                        onChange={e => setSocial(key, { url: e.target.value })}
                        placeholder={`https://... official ${label} URL`}
                      />
                    </div>
                    <div className="col-span-3 md:col-span-2 flex items-center gap-2">
                      <Switch data-testid={`social-${key}-toggle`} checked={!!v.enabled} onCheckedChange={c => setSocial(key, { enabled: c })} />
                      <span className="text-xs text-slate-500">{v.enabled ? "Enabled" : "Disabled"}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <Button data-testid="admin-save-settings" onClick={saveSettings} className="bg-slate-900 text-white">Save Settings</Button>
        </TabsContent>

        <TabsContent value="audit" className="mt-6">
          <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 divide-y divide-slate-200 dark:divide-slate-800 text-sm">
            {audit.map(a => (
              <div key={a.id} className="px-4 py-2 flex justify-between">
                <div><span className="font-bold">{a.action}</span> <span className="text-slate-500 text-xs">· {a.entity_type}:{a.entity_id?.slice(0, 8)}</span></div>
                <div className="text-xs text-slate-500">{a.actor_email || "system"} · {new Date(a.created_at).toLocaleString()}</div>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {editing && <BlogEditor initial={editing} onClose={() => { setEditing(null); reload(); }} />}
      {activeDispute && (
        <DisputeReviewModal
          dispute={activeDispute}
          onClose={() => setActiveDispute(null)}
          onResolve={resolveDispute}
          onStatus={changeDisputeStatus}
        />
      )}
    </div>
  );
}

// --------- Dispute Review Modal ---------
function DisputeReviewModal({ dispute, onClose, onResolve, onStatus }) {
  const [resolution, setResolution] = useState("buyer_refund_full");
  const [refundAmt, setRefundAmt] = useState(0);
  const [note, setNote] = useState("");
  const totalHold = dispute.payout_hold_amount || 0;
  return (
    <Dialog open={true} onOpenChange={() => onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="dispute-review-modal">
        <DialogHeader>
          <DialogTitle>Dispute #{dispute.id.slice(0, 8)} — {dispute.status.replace(/_/g," ")}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid md:grid-cols-2 gap-3 text-sm">
            <Stat k="Listing" v={dispute.listing_title} />
            <Stat k="Reason" v={dispute.reason.replace(/_/g, " ")} />
            <Stat k="Buyer" v={dispute.buyer_name} />
            <Stat k="Seller" v={dispute.seller_name} />
            <Stat k="Winning bid" v={inr(dispute.order?.winning_bid || 0)} />
            <Stat k="Payout hold" v={inr(totalHold)} tone="amber" />
          </div>
          <div className="rounded-lg bg-slate-50 dark:bg-slate-800 p-3 text-sm">
            <div className="font-bold mb-1">Buyer's description</div>
            <div className="text-slate-700 dark:text-slate-300">{dispute.description || "—"}</div>
          </div>
          {(dispute.buyer_evidence || []).length > 0 && (
            <div>
              <div className="text-xs font-bold uppercase text-slate-500 tracking-wider mb-2">Buyer evidence</div>
              <div className="grid grid-cols-4 gap-2">{dispute.buyer_evidence.map((p) => <img key={p} src={resolveImg(p)} alt="" className="aspect-square object-cover rounded-lg" />)}</div>
            </div>
          )}
          {(dispute.seller_evidence || []).length > 0 && (
            <div>
              <div className="text-xs font-bold uppercase text-slate-500 tracking-wider mb-2">Seller evidence</div>
              <div className="grid grid-cols-4 gap-2">{dispute.seller_evidence.map((p) => <img key={p} src={resolveImg(p)} alt="" className="aspect-square object-cover rounded-lg" />)}</div>
            </div>
          )}
          {dispute.ledger && (
            <details className="rounded-lg border border-slate-200 dark:border-slate-800 p-3">
              <summary className="cursor-pointer font-bold text-sm">Financial ledger ({dispute.ledger.length})</summary>
              <table className="w-full text-xs mt-2">
                <thead className="text-slate-500"><tr><th className="text-left">Type</th><th className="text-left">From → To</th><th className="text-right">Amount</th><th className="text-left">When</th></tr></thead>
                <tbody>
                  {dispute.ledger.map(l => (
                    <tr key={l.id} className="border-t border-slate-100 dark:border-slate-800">
                      <td className="py-1">{l.entry_type.replace(/_/g," ")}</td>
                      <td className="py-1">{l.from_party} → {l.to_party}</td>
                      <td className="py-1 text-right font-mono">{inr(l.amount)}</td>
                      <td className="py-1 text-slate-500">{new Date(l.created_at).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </details>
          )}
          <div className="border-t border-slate-200 dark:border-slate-700 pt-4 space-y-3">
            <div className="flex flex-wrap gap-2">
              {["under_review","seller_response_required","buyer_response_required","awaiting_evidence"].map(s => (
                <Button key={s} size="sm" variant="outline" onClick={() => onStatus(s)}>{s.replace(/_/g," ")}</Button>
              ))}
            </div>
            <div className="rounded-lg border border-slate-200 dark:border-slate-800 p-3 space-y-3">
              <div className="font-bold text-sm">Resolve dispute</div>
              <Select value={resolution} onValueChange={setResolution}>
                <SelectTrigger data-testid="resolution-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="buyer_refund_full">Buyer refund — Full</SelectItem>
                  <SelectItem value="buyer_refund_partial">Buyer refund — Partial</SelectItem>
                  <SelectItem value="release_to_seller">Release payout to seller</SelectItem>
                  <SelectItem value="cancelled_no_action">Cancel dispute (no action)</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
              {resolution === "buyer_refund_partial" && (
                <Input type="number" placeholder="Refund amount (₹)" value={refundAmt} onChange={e => setRefundAmt(e.target.value)} data-testid="refund-amount" />
              )}
              <Textarea rows={2} placeholder="Resolution note (visible to both parties)" value={note} onChange={e => setNote(e.target.value)} />
              <Button data-testid="resolve-btn" onClick={() => onResolve(resolution, refundAmt, note)} className="bg-slate-900 text-white">Apply resolution</Button>
            </div>
          </div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Close</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Stat({ k, v, tone, testid }) {
  return (
    <div data-testid={testid} className={`rounded-2xl border p-5 ${tone === "amber" ? "border-amber-300 bg-amber-50 dark:bg-amber-950/30" : "border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900"}`}>
      <div className="text-xs uppercase font-bold text-slate-500 tracking-wider">{k}</div>
      <div className="text-2xl font-extrabold mt-1">{v}</div>
    </div>
  );
}
function Setting({ label, v, onChange }) {
  return (
    <div>
      <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">{label}</label>
      <input type="number" value={v ?? ""} onChange={e => onChange(e.target.value)} className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950" />
    </div>
  );
}

// --------- Blog Editor ---------
function BlogEditor({ initial, onClose }) {
  const [form, setForm] = useState({ ...initial, tags: (initial.tags || []).join(", ") });
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(false);
  const editorRef = React.useRef(null);
  const contentRef = React.useRef(initial.content || "");
  const isNew = !initial.id;
  // Callback ref: fills the editor whenever it mounts (dialog portal / after preview toggle).
  const attachEditor = React.useCallback((el) => {
    editorRef.current = el;
    if (el) el.innerHTML = DOMPurify.sanitize(contentRef.current || "");
  }, []);

  const set = (k, v) => {
    if (k === "content") contentRef.current = v;
    setForm(f => ({ ...f, [k]: v }));
  };
  const exec = (cmd, val = null) => { document.execCommand(cmd, false, val); editorRef.current?.focus(); syncContent(); };
  const syncContent = () => set("content", editorRef.current?.innerHTML || "");

  const addLink = () => { const u = prompt("URL:"); if (u) exec("createLink", u); };
  const addImage = () => { const u = prompt("Image URL:"); if (u) exec("insertImage", u); };
  const insertHeading = (t) => exec("formatBlock", t);
  const insertAuction = async () => {
    const q = prompt("Search live auctions (title or paste listing ID):");
    if (!q) return;
    try {
      // if looks like a UUID, use directly
      let id = q.trim();
      if (!/^[a-f0-9-]{20,}$/i.test(id)) {
        const r = await api.get("/listings", { params: { q, limit: 5, status: "live" } });
        if (!r.data.length) { toast.error("No matching live auctions"); return; }
        const choices = r.data.map((l, i) => `${i + 1}. ${l.title} (${l.id})`).join("\n");
        const pick = prompt(`Pick auction:\n${choices}\n\nEnter number 1–${r.data.length}:`);
        const idx = Number(pick) - 1;
        if (isNaN(idx) || !r.data[idx]) return;
        id = r.data[idx].id;
      }
      // Insert as a plain text token; renderer replaces at read time
      const token = `[[auction:${id}]]`;
      document.execCommand("insertText", false, `\n${token}\n`);
      editorRef.current?.focus();
      syncContent();
      toast.success("Auction embed inserted", { description: "Live card renders on the published page." });
    } catch { toast.error("Could not search auctions"); }
  };

  const save = async (statusOverride) => {
    setSaving(true);
    try {
      const payload = {
        title: form.title,
        excerpt: form.excerpt,
        content: editorRef.current?.innerHTML || form.content || "",
        category: form.category,
        author: form.author || "AuctionBulls Team",
        tags: typeof form.tags === "string" ? form.tags.split(",").map(t => t.trim()).filter(Boolean) : form.tags,
        featured_image: form.featured_image || null,
        featured: !!form.featured,
        status: statusOverride || form.status || "draft",
        seo_title: form.seo_title || form.title,
        seo_description: form.seo_description || form.excerpt,
        scheduled_at: form.scheduled_at || null,
      };
      if (isNew) {
        payload.slug = form.slug || undefined;
        await api.post("/admin/blog/posts", payload);
      } else {
        await api.put(`/admin/blog/posts/${initial.id}`, payload);
      }
      toast.success(isNew ? "Article created" : "Article saved");
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={true} onOpenChange={() => onClose()}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="blog-editor">
        <DialogHeader>
          <DialogTitle>{isNew ? "New Article" : "Edit Article"}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div><Label>Title</Label><Input data-testid="blog-title" value={form.title} onChange={e => set("title", e.target.value)} placeholder="Article title" /></div>
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <Label>Category</Label>
              <Select value={form.category} onValueChange={v => set("category", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{BLOG_CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Status</Label>
              <Select value={form.status} onValueChange={v => set("status", v)}>
                <SelectTrigger data-testid="blog-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="scheduled">Scheduled</SelectItem>
                  <SelectItem value="published">Published</SelectItem>
                  <SelectItem value="unpublished">Unpublished</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Featured?</Label>
              <div className="mt-2 flex items-center gap-2"><Switch checked={!!form.featured} onCheckedChange={c => set("featured", c)} /> <span className="text-sm">{form.featured ? "Yes" : "No"}</span></div>
            </div>
          </div>

          <div><Label>Featured image URL</Label><Input value={form.featured_image || ""} onChange={e => set("featured_image", e.target.value)} placeholder="https://..." /></div>
          <div><Label>Excerpt</Label><Textarea rows={2} value={form.excerpt || ""} onChange={e => set("excerpt", e.target.value)} placeholder="One-line summary" /></div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <Label>Content</Label>
              <div className="flex gap-1">
                <button onClick={() => setPreview(p => !p)} className="text-xs px-2 py-1 rounded border">{preview ? "Edit" : "Preview"}</button>
              </div>
            </div>
            {!preview && (
              <>
                <div className="flex flex-wrap gap-1 border border-slate-200 dark:border-slate-800 rounded-t-lg px-2 py-1 bg-slate-50 dark:bg-slate-900">
                  <ToolBtn onClick={() => insertHeading("h2")} title="Heading 2"><Heading2 className="h-4 w-4" /></ToolBtn>
                  <ToolBtn onClick={() => insertHeading("h3")} title="Heading 3"><Heading3 className="h-4 w-4" /></ToolBtn>
                  <ToolBtn onClick={() => exec("bold")} title="Bold"><Bold className="h-4 w-4" /></ToolBtn>
                  <ToolBtn onClick={() => exec("italic")} title="Italic"><Italic className="h-4 w-4" /></ToolBtn>
                  <ToolBtn onClick={() => exec("insertUnorderedList")} title="Bullet list"><List className="h-4 w-4" /></ToolBtn>
                  <ToolBtn onClick={() => exec("insertOrderedList")} title="Numbered list"><ListOrdered className="h-4 w-4" /></ToolBtn>
                  <ToolBtn onClick={() => insertHeading("blockquote")} title="Quote"><Quote className="h-4 w-4" /></ToolBtn>
                  <ToolBtn onClick={addLink} title="Link"><LinkIcon className="h-4 w-4" /></ToolBtn>
                  <ToolBtn onClick={addImage} title="Image"><ImageIcon className="h-4 w-4" /></ToolBtn>
                  <div className="w-px h-6 bg-slate-300 dark:bg-slate-700 mx-1" />
                  <button type="button" onClick={insertAuction} data-testid="insert-auction-btn"
                    className="px-2 h-8 rounded text-xs font-semibold bg-amber-500 hover:bg-amber-400 text-slate-900 inline-flex items-center gap-1">
                    <Gavel className="h-3.5 w-3.5" /> Insert Auction
                  </button>
                </div>
                <div
                  ref={attachEditor}
                  data-testid="blog-editor-content"
                  contentEditable
                  suppressContentEditableWarning
                  onBlur={syncContent}
                  onInput={syncContent}
                  className="min-h-[280px] p-4 border border-t-0 border-slate-200 dark:border-slate-800 rounded-b-lg prose-blog bg-white dark:bg-slate-900 focus:outline-none"
                />
              </>
            )}
            {preview && (
              <div className="min-h-[280px] p-4 border border-slate-200 dark:border-slate-800 rounded-lg bg-white dark:bg-slate-900 prose-blog"
                   dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(form.content || "") }} />
            )}
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div><Label>Tags (comma-separated)</Label><Input value={form.tags} onChange={e => set("tags", e.target.value)} placeholder="property, guide, mumbai" /></div>
            <div><Label>Author</Label><Input value={form.author || "AuctionBulls Team"} onChange={e => set("author", e.target.value)} /></div>
            <div><Label>SEO title</Label><Input value={form.seo_title || ""} onChange={e => set("seo_title", e.target.value)} /></div>
            <div><Label>SEO description</Label><Input value={form.seo_description || ""} onChange={e => set("seo_description", e.target.value)} /></div>
            {form.status === "scheduled" && (
              <div><Label>Scheduled at (ISO datetime)</Label><Input value={form.scheduled_at || ""} onChange={e => set("scheduled_at", e.target.value)} placeholder="2026-03-01T09:00:00Z" /></div>
            )}
            {isNew && <div><Label>Custom slug (optional)</Label><Input value={form.slug || ""} onChange={e => set("slug", e.target.value)} placeholder="auto-generated" /></div>}
          </div>
        </div>

        <DialogFooter className="gap-2 mt-4">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button variant="outline" onClick={() => save("draft")} disabled={saving}>Save Draft</Button>
          <Button data-testid="blog-publish-btn" onClick={() => save("published")} disabled={saving || !form.title} className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold">
            {saving ? "Saving..." : "Publish"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ToolBtn({ children, onClick, title }) {
  return (
    <button type="button" title={title} onMouseDown={e => e.preventDefault()} onClick={onClick}
      className="h-8 w-8 grid place-items-center rounded hover:bg-slate-200 dark:hover:bg-slate-800">
      {children}
    </button>
  );
}
